import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiFetch, storeToken, getToken, clearToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const tok = getToken();
      if (!tok) {
        setUser(null);
        return;
      }
      const u = await apiFetch("/auth/me");
      setUser(u);
    } catch {
      clearToken();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const signup = async (email, password, name, role = "user", acceptedTerms = false, extra = {}) => {
    const res = await apiFetch(
      "/auth/signup",
      {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          name,
          role,
          accepted_terms: acceptedTerms,
          ref: extra.ref || null,
          city: extra.city || null,
        }),
      },
      false
    );
    storeToken(res.token);
    setUser(res.user);
    return res.user;
  };

  const login = async (email, password) => {
    const res = await apiFetch(
      "/auth/login",
      { method: "POST", body: JSON.stringify({ email, password }) },
      false
    );
    storeToken(res.token);
    setUser(res.user);
    return res.user;
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  const verifyEmail = async (code) => {
    const res = await apiFetch("/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
    storeToken(res.token);
    setUser(res.user);
    return res.user;
  };

  const resendVerification = async () => {
    return apiFetch("/auth/resend-verification", { method: "POST" });
  };

  const updateProfile = async (payload) => {
    const res = await apiFetch("/auth/me", {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    storeToken(res.token);
    setUser(res.user);
    return res;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, signup, login, logout, refresh, verifyEmail, resendVerification, updateProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const c = useContext(AuthContext);
  if (!c) throw new Error("useAuth must be used within AuthProvider");
  return c;
}
